import * as THREE from 'three';

/**
 * System for generating and animating clouds in the sky
 */
export class CloudSystem {
  private scene: THREE.Scene;
  private clouds: THREE.Mesh[] = [];
  private cloudMaterial: THREE.MeshStandardMaterial;
  private cloudGeometries: THREE.BufferGeometry[] = [];
  private boundarySize: number = 500; // Size of the area where clouds can appear
  private cloudCount: number = 25; // Increased number of clouds
  private minCloudHeight: number = 50; // Minimum height for clouds
  private maxCloudHeight: number = 120; // Increased maximum height for more variation
  private minCloudSpeed: number = 3; // Minimum cloud movement speed
  private maxCloudSpeed: number = 4; // Maximum cloud movement speed
  private cloudSpeeds: number[] = []; // Speed for each cloud
  private cloudDirections: THREE.Vector3[] = []; // Direction for each cloud
  private mainDirection: THREE.Vector3; // Main direction for all clouds
  
  /**
   * Creates a new CloudSystem
   * @param scene The scene to add clouds to
   */
  constructor(scene: THREE.Scene) {
    this.scene = scene;
    
    // Create cloud material
    this.cloudMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.9,
      metalness: 0.1,
      transparent: true,
      opacity: 0.8,
      side: THREE.DoubleSide,
    });
    
    // Set main direction for all clouds (slightly diagonal)
    this.mainDirection = new THREE.Vector3(-1, 0, 0.2).normalize();
    
    // Create cloud geometries of different shapes
    this.createCloudGeometries();
    
    // Generate clouds
    this.generateClouds();
  }
  
  /**
   * Creates different cloud geometries for variety
   */
  private createCloudGeometries(): void {
    // Create several different cloud shapes for variety
    
    // Simple cloud shape made of merged spheres
    const cloudGeo1 = new THREE.BufferGeometry();
    const positions: number[] = [];
    const indices: number[] = [];
    const spherePositions = [
      [0, 0, 0, 1.0], // Center, larger
      [1.2, 0.3, 0, 0.8], // Right
      [-1.0, 0.1, 0.2, 0.7], // Left
      [0.5, 0.5, 0.5, 0.6], // Top right
      [-0.5, 0.7, -0.3, 0.7], // Top left
      [0, 0.2, 1.0, 0.8], // Front
      [0.2, 0.3, -1.1, 0.7], // Back
    ];
    
    let vertexCount = 0;
    
    // Create merged geometry from spheres
    spherePositions.forEach(([x, y, z, scale]) => {
      const sphereGeo = new THREE.SphereGeometry(scale, 8, 6);
      const matrix = new THREE.Matrix4().makeTranslation(x, y, z);
      sphereGeo.applyMatrix4(matrix);
      
      // Add vertices
      const spherePositions = sphereGeo.getAttribute('position').array;
      for (let i = 0; i < spherePositions.length; i++) {
        positions.push(spherePositions[i]);
      }
      
      // Add indices
      const sphereIndices = sphereGeo.getIndex()?.array;
      if (sphereIndices) {
        for (let i = 0; i < sphereIndices.length; i++) {
          indices.push(sphereIndices[i] + vertexCount);
        }
      }
      
      vertexCount += spherePositions.length / 3;
    });
    
    cloudGeo1.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    cloudGeo1.setIndex(indices);
    cloudGeo1.computeVertexNormals();
    
    // Flatter cloud shape
    const cloudGeo2 = new THREE.BufferGeometry();
    const positions2: number[] = [];
    const indices2: number[] = [];
    const spherePositions2 = [
      [0, 0, 0, 1.2], // Center, larger
      [1.5, 0, 0, 0.9], // Right
      [-1.5, 0, 0, 0.9], // Left
      [0.8, 0, 0.8, 0.8], // Front right
      [-0.8, 0, 0.8, 0.8], // Front left
      [0.8, 0, -0.8, 0.8], // Back right
      [-0.8, 0, -0.8, 0.8], // Back left
      [0, 0, 1.5, 0.9], // Front
      [0, 0, -1.5, 0.9], // Back
    ];
    
    let vertexCount2 = 0;
    
    // Create merged geometry from spheres
    spherePositions2.forEach(([x, y, z, scale]) => {
      const sphereGeo = new THREE.SphereGeometry(scale, 8, 6);
      const matrix = new THREE.Matrix4().makeTranslation(x, y, z);
      sphereGeo.applyMatrix4(matrix);
      
      // Add vertices
      const spherePositions = sphereGeo.getAttribute('position').array;
      for (let i = 0; i < spherePositions.length; i++) {
        positions2.push(spherePositions[i]);
      }
      
      // Add indices
      const sphereIndices = sphereGeo.getIndex()?.array;
      if (sphereIndices) {
        for (let i = 0; i < sphereIndices.length; i++) {
          indices2.push(sphereIndices[i] + vertexCount2);
        }
      }
      
      vertexCount2 += spherePositions.length / 3;
    });
    
    cloudGeo2.setAttribute('position', new THREE.Float32BufferAttribute(positions2, 3));
    cloudGeo2.setIndex(indices2);
    cloudGeo2.computeVertexNormals();
    
    // Large puffy cloud shape
    const cloudGeo3 = new THREE.BufferGeometry();
    const positions3: number[] = [];
    const indices3: number[] = [];
    const spherePositions3 = [
      [0, 0, 0, 1.5], // Center, larger
      [1.8, 0.4, 0.2, 1.2], // Right
      [-1.8, 0.3, 0.1, 1.3], // Left
      [0.9, 0.8, 0.3, 1.1], // Top right
      [-0.9, 0.9, 0.2, 1.0], // Top left
      [0.3, 1.2, 0.1, 0.9], // Top center
      [0.1, 0.3, 1.7, 1.1], // Front
      [0.2, 0.4, -1.6, 1.0], // Back
      [1.2, 0.2, 1.2, 0.9], // Front right
      [-1.3, 0.3, 1.1, 0.8], // Front left
      [1.1, 0.1, -1.3, 0.9], // Back right
      [-1.2, 0.2, -1.2, 0.8], // Back left
    ];
    
    let vertexCount3 = 0;
    
    // Create merged geometry from spheres
    spherePositions3.forEach(([x, y, z, scale]) => {
      const sphereGeo = new THREE.SphereGeometry(scale, 8, 6);
      const matrix = new THREE.Matrix4().makeTranslation(x, y, z);
      sphereGeo.applyMatrix4(matrix);
      
      // Add vertices
      const spherePositions = sphereGeo.getAttribute('position').array;
      for (let i = 0; i < spherePositions.length; i++) {
        positions3.push(spherePositions[i]);
      }
      
      // Add indices
      const sphereIndices = sphereGeo.getIndex()?.array;
      if (sphereIndices) {
        for (let i = 0; i < sphereIndices.length; i++) {
          indices3.push(sphereIndices[i] + vertexCount3);
        }
      }
      
      vertexCount3 += spherePositions.length / 3;
    });
    
    cloudGeo3.setAttribute('position', new THREE.Float32BufferAttribute(positions3, 3));
    cloudGeo3.setIndex(indices3);
    cloudGeo3.computeVertexNormals();
    
    // Long stretched cloud shape
    const cloudGeo4 = new THREE.BufferGeometry();
    const positions4: number[] = [];
    const indices4: number[] = [];
    const spherePositions4 = [
      [0, 0, 0, 1.0], // Center
      [2.5, 0.1, 0, 0.9], // Far right
      [1.2, 0.2, 0, 0.8], // Right
      [-1.2, 0.1, 0, 0.8], // Left
      [-2.5, 0.2, 0, 0.9], // Far left
      [1.8, 0.3, 0.6, 0.7], // Right front
      [-1.8, 0.3, 0.6, 0.7], // Left front
      [1.8, 0.2, -0.6, 0.7], // Right back
      [-1.8, 0.2, -0.6, 0.7], // Left back
      [0, 0.5, 0, 0.8], // Top center
    ];
    
    let vertexCount4 = 0;
    
    // Create merged geometry from spheres
    spherePositions4.forEach(([x, y, z, scale]) => {
      const sphereGeo = new THREE.SphereGeometry(scale, 8, 6);
      const matrix = new THREE.Matrix4().makeTranslation(x, y, z);
      sphereGeo.applyMatrix4(matrix);
      
      // Add vertices
      const spherePositions = sphereGeo.getAttribute('position').array;
      for (let i = 0; i < spherePositions.length; i++) {
        positions4.push(spherePositions[i]);
      }
      
      // Add indices
      const sphereIndices = sphereGeo.getIndex()?.array;
      if (sphereIndices) {
        for (let i = 0; i < sphereIndices.length; i++) {
          indices4.push(sphereIndices[i] + vertexCount4);
        }
      }
      
      vertexCount4 += spherePositions.length / 3;
    });
    
    cloudGeo4.setAttribute('position', new THREE.Float32BufferAttribute(positions4, 3));
    cloudGeo4.setIndex(indices4);
    cloudGeo4.computeVertexNormals();
    
    // Add geometries to array
    this.cloudGeometries.push(cloudGeo1, cloudGeo2, cloudGeo3, cloudGeo4);
  }
  
  /**
   * Generates clouds and adds them to the scene
   */
  private generateClouds(): void {
    for (let i = 0; i < this.cloudCount; i++) {
      // Choose a random cloud geometry
      const geometryIndex = Math.floor(Math.random() * this.cloudGeometries.length);
      const cloudGeometry = this.cloudGeometries[geometryIndex];
      
      // Create cloud mesh
      const cloud = new THREE.Mesh(cloudGeometry, this.cloudMaterial.clone());
      
      // Random position within boundary
      const x = (Math.random() - 0.5) * this.boundarySize * 2;
      const z = (Math.random() - 0.5) * this.boundarySize * 2;
      const y = this.minCloudHeight + Math.random() * (this.maxCloudHeight - this.minCloudHeight);
      cloud.position.set(x, y, z);
      
      // Random rotation
      cloud.rotation.y = Math.random() * Math.PI * 2;
      
      // Random scale (make clouds bigger and more varied)
      const scale = 8 + Math.random() * 15;
      
      // Add some random squashing/stretching for more variety
      const scaleY = 0.5 + Math.random() * 0.3; // Vertical scale factor
      const scaleXZ = 0.8 + Math.random() * 0.4; // Horizontal scale variation
      
      cloud.scale.set(
        scale * (scaleXZ + (Math.random() * 0.4 - 0.2)), 
        scale * scaleY, 
        scale * (scaleXZ + (Math.random() * 0.4 - 0.2))
      );
      
      // Slightly vary the opacity for more depth
      const material = cloud.material as THREE.MeshStandardMaterial;
      material.opacity = 0.7 + Math.random() * 0.3;
      
      // Speed with small variation
      const speed = this.minCloudSpeed + Math.random() * (this.maxCloudSpeed - this.minCloudSpeed);
      this.cloudSpeeds.push(speed);
      
      // Direction with small variation from main direction
      const variation = 0.1; // Small variation factor
      const direction = new THREE.Vector3(
        this.mainDirection.x + (Math.random() * variation * 2 - variation),
        0,
        this.mainDirection.z + (Math.random() * variation * 2 - variation)
      ).normalize();
      this.cloudDirections.push(direction);
      
      // Add to scene and array
      this.scene.add(cloud);
      this.clouds.push(cloud);
    }
  }
  
  /**
   * Updates cloud positions
   * @param deltaTime Time since last update in seconds
   */
  public update(deltaTime: number): void {
    // Update each cloud
    for (let i = 0; i < this.clouds.length; i++) {
      const cloud = this.clouds[i];
      const speed = this.cloudSpeeds[i];
      const direction = this.cloudDirections[i];
      
      // Move cloud
      cloud.position.x += direction.x * speed * deltaTime;
      cloud.position.z += direction.z * speed * deltaTime;
      
      // Check if cloud is out of bounds
      const halfBoundary = this.boundarySize;
      if (
        cloud.position.x > halfBoundary ||
        cloud.position.x < -halfBoundary ||
        cloud.position.z > halfBoundary ||
        cloud.position.z < -halfBoundary
      ) {
        // Reset cloud position to the opposite side
        if (Math.abs(cloud.position.x) > Math.abs(cloud.position.z)) {
          // Reset X position
          cloud.position.x = -Math.sign(cloud.position.x) * halfBoundary;
          // Randomize Z position
          cloud.position.z = (Math.random() - 0.5) * this.boundarySize;
        } else {
          // Reset Z position
          cloud.position.z = -Math.sign(cloud.position.z) * halfBoundary;
          // Randomize X position
          cloud.position.x = (Math.random() - 0.5) * this.boundarySize;
        }
      }
    }
  }
  
  /**
   * Disposes of cloud resources
   */
  public dispose(): void {
    // Remove clouds from scene and dispose of resources
    for (const cloud of this.clouds) {
      this.scene.remove(cloud);
    }
    
    // Dispose of geometries
    for (const geometry of this.cloudGeometries) {
      geometry.dispose();
    }
    
    // Dispose of material
    this.cloudMaterial.dispose();
    
    // Clear arrays
    this.clouds = [];
    this.cloudGeometries = [];
    this.cloudSpeeds = [];
    this.cloudDirections = [];
  }
} 